/**
 * Application router (BUILD_SPEC.md §10.1).
 *
 * Routes wired today:
 *   /                       → redirect (role home: /wall for DISPLAY, else /dashboard)
 *   /login                  → public; bounces to the role's home if authed
 *   /dashboard              → protected; standard card grid (ADMIN + EMPLOYEE)
 *   /wall                   → protected; DISPLAY-only TV Permit Wall (full-screen)
 *   /admin/transactions     → protected, admin-only
 *   /admin/users            → protected, admin-only
 *   /admin/archive          → protected, admin-only
 *   /admin/settings         → protected, admin-only
 *   /admin/audit            → protected, admin-only
 *
 * `AdminLayout` enforces the admin role check; the auth guard on the
 * outer route guarantees a logged-in user. `AppLayout` additionally
 * redirects DISPLAY sessions to /wall so the kiosk never reaches the grid.
 */

import { createBrowserRouter, Navigate } from "react-router-dom";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { WallPage } from "@/pages/WallPage";
import { TransactionDetailPage } from "@/pages/TransactionDetailPage";
import { AppLayout } from "@/components/AppLayout";
import { AdminLayout } from "@/components/AdminLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { RedirectIfAuthed } from "@/components/RedirectIfAuthed";
import { HomeRedirect } from "@/components/HomeRedirect";
import { TransactionsAdminPage } from "@/pages/admin/TransactionsAdminPage";
import { UsersAdminPage } from "@/pages/admin/UsersAdminPage";
import { SettingsAdminPage } from "@/pages/admin/SettingsAdminPage";
import { ArchiveAdminPage } from "@/pages/admin/ArchiveAdminPage";
import { AuditAdminPage } from "@/pages/admin/AuditAdminPage";
import { ImportAdminPage } from "@/pages/admin/ImportAdminPage";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <HomeRedirect />,
  },
  {
    path: "/login",
    element: (
      <RedirectIfAuthed>
        <LoginPage />
      </RedirectIfAuthed>
    ),
  },
  {
    // DISPLAY-only TV Permit Wall. Full-screen, outside AppLayout (it owns
    // its own fixed shell and palette, no app header). RequireAuth still
    // guards it; ADMIN/EMPLOYEE may open it to preview/set up the TV.
    path: "/wall",
    element: (
      <RequireAuth>
        <WallPage />
      </RequireAuth>
    ),
  },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { path: "/dashboard", element: <DashboardPage /> },
      // v1.1 B4: full action-history detail. ADMIN + EMPLOYEE read it;
      // admin-only operations (export) gate inside the page. DISPLAY is
      // wall-only and never reaches AppLayout (redirected to /wall).
      { path: "/transactions/:id", element: <TransactionDetailPage /> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <AdminLayout />
      </RequireAuth>
    ),
    children: [
      { path: "/admin", element: <Navigate to="/admin/transactions" replace /> },
      { path: "/admin/transactions", element: <TransactionsAdminPage /> },
      { path: "/admin/users", element: <UsersAdminPage /> },
      { path: "/admin/import", element: <ImportAdminPage /> },
      { path: "/admin/archive", element: <ArchiveAdminPage /> },
      { path: "/admin/settings", element: <SettingsAdminPage /> },
      { path: "/admin/audit", element: <AuditAdminPage /> },
    ],
  },
  {
    path: "*",
    element: <HomeRedirect />,
  },
]);
